import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  DynatraceService,
  PushDatalayersGoogleTagManager,
  ResponseFormatAddress,
  SuraFormatAddressService,
  SuraFormValidators,
} from '@segurosvoluntarios_vel/commons-services';
import { OptionInput } from '@segurosvoluntarios_vel/lib-angular-components/src/sura-form';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { FormPago } from 'src/app/pago/enums/FormPago.enum';
import { PersonaQuePaga } from 'src/app/pago/enums/PersonaQuePaga.enum';
import { TarjetaDeCreditoFormValidators } from 'src/app/pago/validators/tarjeta-de-credito-form.validator';
import {
  getCiudades,
  getDepartamentos,
  getTiposDeDocumento,
} from 'src/app/store/actions/maestros.action';
import {
  changeRenovacionAutomatica,
  registerPago,
} from 'src/app/store/actions/pago.action';
import { PagoBuilder } from 'src/app/pago/builders/pago/pago.builder';
import { TarjetaDeCreditoBuilder } from 'src/app/pago/builders/TarjetaDeCreditoBuilder/TarjetaDeCredito.builder';
import { PagadorBuilder } from 'src/app/pago/builders/PagadorBuilder/Pagador.builder';
import { MedioDePago } from 'src/app/pago/enums/MedioDePago.enum';
import { Pais } from 'src/app/pago/enums/Pais.enum';
import { ConfiguracionDePago } from 'src/app/pago/models/ConfiguracionDePago.model';
import { DatalayerEvent } from 'src/app/pago/models/DatalayerEvent.model';
import { Pagador } from 'src/app/pago/models/Pagador.model';
import { Pago } from 'src/app/pago/models/Pago.model';
import { TarjetaDeCredito } from 'src/app/pago/models/TarjetaDeCredito.model';
import { PagoFacade } from 'src/app/pago/services/pago-facade/pago.facade';
import { MaestroFacade } from 'src/app/pago/services/maestro-facade/maestro.facade';
import { Ciudad } from 'src/app/pago/models/Ciudad.model';
import { Departamento } from 'src/app/pago/models/Departamento.model';
import { ResumenDescuentosFacade } from 'src/app/pago/services/resumen-descuentos/resumen-descuentos-facade.service';
import { DatalayerRegisterPagoService } from 'src/app/pago/services/dalayer-register/datalayer-register-pago.service';
import { ContenidoFacade } from 'src/app/pago/services/content-facade/content-facade.service';
import { ManagerTarjetaDeCreditoService } from 'src/app/pago/services/manager-credit/manager-credit-card.service';

@Component({
  selector: 'app-tarjeta-de-credito',
  templateUrl: './tarjeta-de-credito.component.html',
  styleUrls: ['./tarjeta-de-credito.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TarjetaDeCreditoComponent implements OnInit, OnDestroy {
  readonly OTRA_PERSONA = PersonaQuePaga.OTRA_PERSONA;
  readonly FORM_PAGO_DATOS_OTRA_PERSONA = FormPago.DATOS_OTRA_PERSONA;
  readonly FORM_PAGO_DATOS_TARJETA_DE_CREDITO =
    FormPago.DATOS_TARJETA_DE_CREDITO;

  form: FormGroup;
  submitted = false;
  optionsTarjetaDeCreditoToPagar: OptionInput[];
  configuracionDePago: ConfiguracionDePago;
  pago: Pago;
  personaQuePaga: string;
  subscription: Subscription = new Subscription();
  departamentos: Departamento[];

  constructor(
    private formBuilder: FormBuilder,
    private pagoFacade: PagoFacade,
    public maestroFacade: MaestroFacade,
    private changeDetectorRef: ChangeDetectorRef,
    private suraFormatAddressService: SuraFormatAddressService,
    public resumenDescuentosFacade: ResumenDescuentosFacade,
    private datalayerRegisterPagoService: DatalayerRegisterPagoService,
    public contenidoFacade: ContenidoFacade,
    private dynatraceService: DynatraceService
  ) {
    this.maestroFacade.dispatch(getTiposDeDocumento());
    this.maestroFacade.dispatch(getCiudades());
    this.maestroFacade.dispatch(getDepartamentos());
  }

  ngOnInit(): void {
    this.subscribeToConfiguracionDePago();
    this.subscribeToPago();
    this.subscribeToDepartamentos();
  }

  subscribeToDepartamentos() {
    this.subscription.add(
      this.maestroFacade.departamentos$.subscribe(
        (departamentos: Departamento[]) => {
          this.departamentos = departamentos;
        }
      )
    );
  }

  subscribeToConfiguracionDePago() {
    this.pagoFacade.configuracionDePago$.subscribe(
      (configuracionDePago: ConfiguracionDePago) => {
        this.configuracionDePago = configuracionDePago;
        this.optionsTarjetaDeCreditoToPagar = this.getOptionsTarjetaDeCreditoToPagar(
          configuracionDePago.pagador
        );
        this.changeDetectorRef.markForCheck();
        this.form = this.createForm();
        this.getFormGroup(this.FORM_PAGO_DATOS_OTRA_PERSONA).disable();
        this.controlTarjetaDeCreditoToPagar.setValue(
          configuracionDePago.pagador
        );
      }
    );
  }

  subscribeToPago() {
    this.subscription.add(
      this.pagoFacade.pago$.subscribe((pago: Pago) => {
        this.pago = pago;
      })
    );
  }

  getOptionsTarjetaDeCreditoToPagar(
    nombrePersonaActual: string
  ): OptionInput[] {
    return [
      { value: nombrePersonaActual, name: nombrePersonaActual },
      { value: this.OTRA_PERSONA, name: 'Otra persona' },
    ];
  }

  createForm(): FormGroup {
    return this.formBuilder.group({
      [FormPago.PERSONA_QUE_PAGA]: [null, [Validators.required]],
      [this
        .FORM_PAGO_DATOS_OTRA_PERSONA]: this.createDatosOtraPersonaFormGroup(),
      [FormPago.DATOS_TARJETA_DE_CREDITO]: this.createDatosTarjetaDeCreditoFormGroup(),
    });
  }

  createDatosOtraPersonaFormGroup(): FormGroup {
    return this.formBuilder.group({
      [FormPago.TIPO_DOCUMENTO]: ['C', Validators.required],
      [FormPago.NUMERO_DOCUMENTO]: [
        null,
        [Validators.required, SuraFormValidators.NumeroDocumentoValidador],
      ],
      [FormPago.NOMBRES]: [
        null,
        [Validators.required, SuraFormValidators.OnlyLettersValidator],
      ],
      [FormPago.APELLIDOS]: [
        null,
        [Validators.required, SuraFormValidators.OnlyLettersValidator],
      ],
      [FormPago.RAZON_SOCIAL]: [{ value: null, disabled: true }],
      [FormPago.CORREO]: [
        null,
        [Validators.required, SuraFormValidators.EmailValidator],
      ],
      [FormPago.CIUDAD]: [null, [Validators.required]],
      [FormPago.TELEFONO]: [
        null,
        [Validators.required, SuraFormValidators.NumeroDeTelefonoValidador],
      ],
      [FormPago.DIRECCION]: [
        { value: null, disabled: true },
        [Validators.required, SuraFormValidators.AddressValidator],
      ],
    });
  }

  createDatosTarjetaDeCreditoFormGroup(): FormGroup {
    const formStructure = {
      [FormPago.NUMERO_DE_TARJETA]: [
        null,
        [Validators.required, TarjetaDeCreditoFormValidators.validarNumero],
      ],
      [FormPago.CODIGO_DE_SEGURIDAD]: [null, [Validators.required]],
      [FormPago.EXPIRATION_MONTH]: [null, Validators.required],
      [FormPago.EXPIRATION_YEAR]: [null, Validators.required],
      [FormPago.CANTIDAD_DE_CUOTAS]: ['1', Validators.required],
    };
    if (this.configuracionDePago.habilitarRenovacionAutomatica) {
      formStructure[FormPago.HABILITAR_RENOVACION_AUTOMATICA] = false;
    }
    return this.formBuilder.group(formStructure);
  }

  getFormGroup(groupName: string): FormGroup {
    return this.form.get(groupName) as FormGroup;
  }

  get controlTarjetaDeCreditoToPagar(): AbstractControl {
    return this.form.get(FormPago.PERSONA_QUE_PAGA);
  }

  handleChangeTarjetaDeCreditoParaPagar(personaQuePaga: string) {
    const form = this.getFormGroup(this.FORM_PAGO_DATOS_OTRA_PERSONA);
    this.personaQuePaga = personaQuePaga;
    if (personaQuePaga === this.OTRA_PERSONA) {
      form.enable();
    } else {
      form.disable();
    }
    form.updateValueAndValidity();
  }

  onSubmit() {
    this.submitted = true;
    if (this.form.valid) {
      this.handlePushDatalayer(
        this.datalayerRegisterPagoService.build(
          this.pago,
          this.configuracionDePago,
          MedioDePago.TARJETA_DE_CREDITO
        )
      );
      this.dynatraceService.enterAction(
        `${MedioDePago.MEDIO_PAGO_SELECCIONADO}: ${MedioDePago.TARJETA_DE_CREDITO}`
      );

      this.pagoFacade.dispatch(
        registerPago({
          pago: this.buildPago(this.personaQuePaga === this.OTRA_PERSONA),
        })
      );
    }
  }

  buildPago(buildWithPagador: boolean): Pago {
    return (buildWithPagador
      ? this.basePagoBuild().withPagador(this.buildPagador())
      : this.basePagoBuild()
    ).build();
  }

  basePagoBuild(): PagoBuilder {
    return new PagoBuilder()
      .withIdPago(this.pago.idPago)
      .withRenovacionAutomatica(this.pago.renovacionAutomatica)
      .withTMedioPago(MedioDePago.TARJETA_DE_CREDITO)
      .withTarjetaCredito(this.buildTarjetaDeCredito())
      .withDescuentosAplicados(this.pago.descuentosAplicados)
      .withTotal(this.configuracionDePago.valor)
      .withSubTotal(this.configuracionDePago.valor)
      .withTotalDescuentos(this.pago.totalDescuentos)
      .withNombreProducto(this.pago.idProducto);
  }

  buildTarjetaDeCredito(): TarjetaDeCredito {
    const form = this.getFormGroup(this.FORM_PAGO_DATOS_TARJETA_DE_CREDITO);
    return new TarjetaDeCreditoBuilder()
      .withFranquicia(
        ManagerTarjetaDeCreditoService.findTipoDeTarjeta(
          form.get(FormPago.NUMERO_DE_TARJETA).value
        ).name
      )
      .withTNumeroTarjeta(form.get(FormPago.NUMERO_DE_TARJETA).value)
      .withCodigoSeguridad(form.get(FormPago.CODIGO_DE_SEGURIDAD).value)
      .withFechaVencimiento(
        this.createFechaVencimiento(
          form.get(FormPago.EXPIRATION_MONTH).value,
          form.get(FormPago.EXPIRATION_YEAR).value
        )
      )
      .withCuotas(form.get(FormPago.CANTIDAD_DE_CUOTAS).value)
      .build();
  }

  getCiudad(): Ciudad {
    return this.getFormGroup(this.FORM_PAGO_DATOS_OTRA_PERSONA).get(
      FormPago.CIUDAD
    ).value as Ciudad;
  }

  buildPagador(): Pagador {
    const form = this.getFormGroup(this.FORM_PAGO_DATOS_OTRA_PERSONA);

    return new PagadorBuilder()
      .withNombres(form.get(FormPago.NOMBRES).value)
      .withApellidos(form.get(FormPago.APELLIDOS).value)
      .withTipoDocumento(form.get(FormPago.TIPO_DOCUMENTO).value)
      .withNumeroDocumento(form.get(FormPago.NUMERO_DOCUMENTO).value)
      .withCiudad(this.getCiudad().nombre)
      .withDepartamento(
        this.departamentos.find(
          departamento =>
            departamento.codigo === this.getCiudad().departamentoCodigo
        ).nombre
      )
      .withDireccion(form.get(FormPago.DIRECCION).value)
      .withPais(Pais.COLOMBIA)
      .withCorreoElectronico(form.get(FormPago.CORREO).value)
      .withTelefono(form.get(FormPago.TELEFONO).value)
      .withCelular(form.get(FormPago.TELEFONO).value)
      .build();
  }

  createFechaVencimiento(
    expirationMonth: string,
    yearExpiration: string
  ): string {
    /* Si el mes de expiración de la tarjeta esta entre 1-9, se debe agregar
    un 0 al inicio ya que así lo requiere el servicio. ejemplo de fecha correcta: 09/2025  */
    return `${
      Number(expirationMonth) < 10 ? '0' + expirationMonth : expirationMonth
    }/${yearExpiration}`;
  }

  handleFormatDireccion() {
    const formTomador = this.getFormGroup(this.FORM_PAGO_DATOS_OTRA_PERSONA);
    const controlDireccion = formTomador.get(FormPago.DIRECCION);
    this.suraFormatAddressService
      .formatAddress(controlDireccion.value, this.getCiudad().nombre)
      .pipe(take(1))
      .subscribe((response: ResponseFormatAddress) => {
        controlDireccion.setValue(response.direccion);
      });
  }

  onActivarRenovacionAutomaticaChange(
    activarRenovacionAutomatica: boolean
  ): void {
    this.pagoFacade.dispatch(
      changeRenovacionAutomatica({ activarRenovacionAutomatica })
    );
  }

  handlePushDatalayer(object: DatalayerEvent) {
    PushDatalayersGoogleTagManager.pushObject(object);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
